import _ from 'lodash';
import Immutable from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';

import { AdminTrash } from 'components/admin/lib/admin_layout';
import Button, { ButtonTypes } from 'components/common/button';
import connect from 'components/lib/connect';
import Err from 'models/err';
import { getErrorMessageList } from 'components/lib/error_handler';
import { H3, H4 } from 'components/common/headers';
import IdGenerator from 'scripts/domain/contracts/id_generator';
import Input from 'components/common/input';
import LoadingSpinner from 'components/lib/loading_spinner';
import { MultiSelectMenu } from 'components/lib/conversation_multi_topic_deprecated';
import OpenWebhooks from 'actions/integrations/webhooks/open_webhooks';
import SaveWebhook from 'actions/integrations/webhooks/save_webhook';
import Toggle from 'components/common/toggle';
import Webhook from 'models/webhook';
import WebhookError from './webhook_error';

const EVENT_OPTIONS = Immutable.fromJS([
  {
    id: 'CONVERSATION/CLOSED',
    name: 'CONVERSATION/CLOSED',
  },
  {
    id: 'CONVERSATION/CREATED',
    name: 'CONVERSATION/CREATED',
  },
  {
    id: 'CONVERSATION_ASSIGNEE/UPDATED',
    name: 'CONVERSATION_ASSIGNEE/UPDATED',
  },
  {
    id: 'CONVERSATION_STATUS/UPDATED',
    name: 'CONVERSATION_STATUS/UPDATED',
  },
  {
    id: 'CUSTOMER_PROFILE/CREATED',
    name: 'CUSTOMER_PROFILE/CREATED',
  },
  {
    id: 'CUSTOMER_PROFILE/DELETED',
    name: 'CUSTOMER_PROFILE/DELETED',
  },
  {
    id: 'CUSTOMER_PROFILE/MERGED',
    name: 'CUSTOMER_PROFILE/MERGED',
    disabled: true,
  },
  {
    id: 'CUSTOMER_PROFILE/UPDATED',
    name: 'CUSTOMER_PROFILE/UPDATED',
  },
  {
    id: 'CUSTOMER/MERGED',
    name: 'CUSTOMER/MERGED',
  },
  {
    id: 'EXPORT_JOB/COMPLETED',
    name: 'EXPORT_JOB/COMPLETED',
  },
  {
    id: 'EXPORT_JOB/FAILED',
    name: 'EXPORT_JOB/FAILED',
  },
  {
    id: 'MESSAGE/RECEIVED',
    name: 'MESSAGE/RECEIVED',
  },
  {
    id: 'TASK/CREATED',
    name: 'TASK/CREATED',
  },
]);

export const HIDDEN_STRING = '•••••••';

export const BoldHeader = styled(H4)`
  font-weight: ${p => p.theme.fontWeight.heavy};
  font-size: ${p => p.theme.fontSize.base};
  margin: ${p => p.theme.spacing.insetMedium} 0 ${p => p.theme.spacing.insetSmall};
`;

export const InputHeader = styled(H4)`
  font-size: ${p => p.theme.fontSize.base};
  margin: ${p => p.theme.spacing.insetMedium} 0 ${p => p.theme.spacing.insetSmall};
`;

const HTTPHeaderSeparator = styled.div`
  align-items: center;
  display: flex;
  height: 30px;
  justify-content: center;
`;

const EmptyHTTPHeaderSeparator = styled.div`
  height: 35px;
`;

const GrayHeader = styled(H4)`
  color: ${p => p.theme.colors.gray600};
  font-size: ${p => p.theme.fontSize.base};
  margin: ${p => p.theme.spacing.insetSmall} 0 ${p => p.theme.spacing.insetMedium};
`;

export const StyledInput = styled(({ hasErrors, ...props }) => <Input {...props} />)`
  border-radius: 4px;
  border: 1px solid ${p => p.theme.colors.gray400};
  background: ${p => p.theme.colors.white};
  min-width: 400px;
  min-height: 30px;
  padding-left: ${p => p.theme.spacing.insetSmall};
  ${props =>
    props.hasErrors &&
    css`
      border-color: ${p => p.theme.colors.red400};
      margin-bottom: ${p => p.theme.spacing.stackSmall};
    `};
  &::placeholder {
    color: ${p => p.theme.colors.gray600};
  }
`;

export const HTTPInput = styled(Input)`
  border-radius: 4px;
  border: 1px solid ${p => p.theme.colors.gray400};
  background: ${p => p.theme.colors.white};
  min-width: 170px;
  margin-bottom: ${p => p.theme.spacing.insetMedium};
  min-height: 30px;
  padding-left: ${p => p.theme.spacing.insetSmall};
  &::placeholder {
    color: ${p => p.theme.colors.gray600};
  }
`;

const WebhooksLink = styled.a`
  color: ${p => p.theme.colors.green400};
  font: ${p => p.theme.fontSize.base};
`;

const LinkContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const RowContainer = styled.div`
  display: flex;
  flex-direction: row;
  &:hover .adminTrash {
    visibility: visible;
  }
`;

export const HeaderTrash = styled(AdminTrash)`
  align-self: center;
  visibility: hidden;
  margin-top: ${p => p.theme.spacing.small};
`;

const ColContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Spacer = styled.div`
  width: 60px;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const FooterSpacer = styled.div`
  height: 70px;
`;

const FooterContainer = styled.div`
  background-color: ${p => p.theme.colors.white};
  border-top: 1px solid ${p => p.theme.colors.gray400};
  bottom: 0;
  display: flex;
  flex-direction: column;
  height: 60px;
  justify-content: center;
  left: 0;
  position: fixed;
  width: 100%;
`;

const FooterButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 0 60px;
`;

const MultiSelectMenuStyled = styled(MultiSelectMenu)`
  height: 30px;
  background-color: ${p => p.theme.colors.white};
  border-radius: 20px;
  border: 1px solid ${p => p.theme.colors.gray400};
  min-width: 400px;
  min-height: 30px;
  padding-right: 3%;
  &::after {
    padding-right: 3%;
    content: '\f107'; // http://fontawesome.io/icon/angle-down/;
  }
  ${props =>
    props.hasErrors &&
    css`
      border-color: ${p => p.theme.colors.red400};
      margin-bottom: ${p => p.theme.spacing.stackSmall};
    `};
`;

export const AddHeaderButton = styled(Button)`
  padding: 0 0;
  text-align: left;
  margin-bottom: ${p => p.theme.spacing.insetXLarge};
`;

export const WebhookHeader = styled(H3)`
  font-weight: ${p => p.theme.fontWeight.heavy};
  margin-top: ${p => p.theme.spacing.insetXLarge};
`;

const Background = styled.div`
  background-color: ${p => p.theme.colors.gray100};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  width: 100%;
  position: absolute;
  overflow: scroll;
`;

export function InputWithErrors({ errors, ...props }) {
  if (!errors) {
    return <StyledInput {...props} />;
  }

  return (
    <div>
      <StyledInput {...props} hasErrors />
      <WebhookError errors={errors} />
    </div>
  );
}

InputWithErrors.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.string),
};

export class WebhookEditor extends React.Component {
  constructor(props) {
    super(props);

    let webhook = this.props.webhook;
    let headers = [];

    headers = _.map(_.get(webhook, 'headersKeys', []), name => ({
      name,
      value: HIDDEN_STRING,
    }));

    if (_.isEmpty(headers)) {
      headers.push({
        name: '',
        value: '',
      });
    }

    this.state = {
      id: _.get(webhook, 'id', IdGenerator.newId()),
      enabled: _.get(webhook, 'enabled', false),
      errors: this.getStateErrorsFromProps(props, headers),
      events: _.get(webhook, 'events', []),
      headers,
      hideCredentials: _.get(webhook, 'hasCredentials', false),
      name: _.get(webhook, 'name', ''),
      password: null,
      realm: null,
      url: _.get(webhook, 'url', ''),
      username: null,
    };

    _.bindAll(this, [
      'addHeaderRow',
      'editEvents',
      'editHeaderName',
      'editHeaderValue',
      'editName',
      'editPassword',
      'editRealm',
      'editUrl',
      'editUsername',
      'handleClickCancel',
      'handleClickSave',
      'removeHeaderRow',
      'renderHeader',
      'renderHeaders',
      'renderEvents',
      'toggleEnabled',
    ]);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({
      errors: this.getStateErrorsFromProps(nextProps, this.state.headers),
    });
  }

  getStateErrorsFromProps(props, headers) {
    return {
      url: getErrorMessageList({ errors: props.errors, attr: 'url' }),
      events: getErrorMessageList({ errors: props.errors, attr: 'events' }),
      name: getErrorMessageList({ errors: props.errors, attr: 'name' }),
      credentials: getErrorMessageList({ errors: props.errors, attr: 'credentials' }),
      headers: getErrorMessageList({ errors: props.errors, attr: 'headers' }),
      headersList: _.map(headers, (headerKV, index) =>
        getErrorMessageList({ errors: props.errors, attr: `headers.${headerKV.name}` })
      ),
    };
  }

  handleClickCancel() {
    this.props.onCancel();
  }

  handleClickSave() {
    let { id, name, events, url, username, password, realm, enabled, headers } = this.state;

    // remove unpopulated or existing redacted header rows
    headers = _.filter(headers, h => (h.name || h.value) && h.value !== HIDDEN_STRING);

    let credentials = Object.assign(
      {},
      username === null ? undefined : { username },
      password === null ? undefined : { password },
      realm === null ? undefined : { realm }
    );

    const webhook = {
      id,
      name,
      enabled,
      events,
      url,
      headers,
    };

    if (!_.isEmpty(credentials)) {
      webhook.credentials = credentials;
    }

    this.setState({
      errors: this.getStateErrorsFromProps({ errors: [] }),
    });

    this.props.onSave(webhook, this.props.isExisting);
  }

  editName(e) {
    this.setState({
      name: e.target.value,
      errors: { ...this.state.errors, name: null },
    });
  }

  editUrl(e) {
    this.setState({
      url: e.target.value,
      errors: { ...this.state.errors, url: null },
    });
  }

  editEvents(selectedEvents) {
    this.setState({ events: selectedEvents, errors: { ...this.state.errors, events: null } });
  }

  editUsername(e) {
    this.setState({ username: e.target.value });
  }

  editPassword(e) {
    this.setState({ password: e.target.value });
  }

  editRealm(e) {
    this.setState({ realm: e.target.value });
  }

  editHeaderName(e) {
    const headerName = e.target.value;
    const idx = e.target.name;
    let { headers } = this.state;
    headers[idx].name = headerName;

    this.setState({ headers });
  }

  editHeaderValue(e) {
    const headerValue = e.target.value;
    const idx = e.target.name;
    let { headers } = this.state;
    headers[idx].value = headerValue;

    this.setState({ headers });
  }

  toggleEnabled(e) {
    this.setState(prevState => ({
      enabled: !prevState.enabled,
    }));
  }

  addHeaderRow() {
    let { headers } = this.state;
    headers.push({ name: '', value: '' });
    this.setState({ headers });
  }

  removeHeaderRow(e) {
    let { headers } = this.state;
    const idx = Number(e.target.id);
    headers = _.filter(headers, (h, i) => i !== idx);
    if (!headers.length) {
      headers = [{ name: '', value: '' }];
    }
    this.setState({ headers });
  }

  renderHeaders() {
    return _.map(this.state.headers, (h, idx) => this.renderHeader(h, idx));
  }

  renderHeader(header, idx) {
    return (
      <div>
        <RowContainer>
          <ColContainer>
            <InputHeader>Header name</InputHeader>
            <HTTPInput
              name={idx}
              onChange={this.editHeaderName}
              placeholder={this.state.hideCredentials ? HIDDEN_STRING : 'name'}
              value={header.name}
            />
          </ColContainer>
          <Spacer>
            <EmptyHTTPHeaderSeparator />
            <HTTPHeaderSeparator>:</HTTPHeaderSeparator>
          </Spacer>
          <ColContainer>
            <InputHeader>Header value</InputHeader>
            <HTTPInput
              name={idx}
              onChange={this.editHeaderValue}
              placeholder={this.state.hideCredentials ? HIDDEN_STRING : 'value'}
              value={header.value}
            />
          </ColContainer>
          <Spacer>
            <EmptyHTTPHeaderSeparator />
            <HeaderTrash id={idx} onClick={this.removeHeaderRow} />
          </Spacer>
        </RowContainer>
        <WebhookError errors={_.get(this.state, `errors.headersList[${idx}]`)} />
      </div>
    );
  }

  renderEvents(events) {
    let errors = _.get(this.state, 'errors.events');
    return (
      <div className="webhookEditor-events">
        <MultiSelectMenuStyled
          hasErrors={!!errors}
          placeholder="Choose an event"
          unitLabelSingular="event"
          unitLabelPlural="events"
          options={EVENT_OPTIONS}
          optionIds={events}
          onFocus={_.noop}
          onOptionChange={this.editEvents}
          dropDown
        />
        <WebhookError errors={errors} />
      </div>
    );
  }

  render() {
    if (this.props.isPending) {
      return <LoadingSpinner isModal />;
    }

    let { url, name, hideCredentials, realm, username, password, events } = this.state;

    return (
      <Background className="webhook-editor">
        <WebhookHeader>{this.props.isExisting ? 'Edit Webhook' : 'Create a Webhook'}</WebhookHeader>
        <div>
          <BoldHeader>Name</BoldHeader>
          <InputWithErrors
            onChange={this.editName}
            placeholder={'ex. Send Survey'}
            value={name}
            errors={_.get(this.state, 'errors.name')}
          />
          <BoldHeader>Webhook URL</BoldHeader>
          <InputWithErrors
            onChange={this.editUrl}
            placeholder="https://"
            value={url}
            errors={_.get(this.state, 'errors.url')}
          />
          <GrayHeader>URL where you'd like to send events</GrayHeader>
          <BoldHeader>Events</BoldHeader>
          {this.renderEvents(events)}
          <BoldHeader>Enabled</BoldHeader>
          <Toggle isEnabled={this.state.enabled} onChange={this.toggleEnabled} />
          <BoldHeader>Basic authentication</BoldHeader>
          <WebhookError errors={_.get(this.state, 'errors.credentials')} />
          <InputHeader>Username</InputHeader>
          <StyledInput
            onChange={this.editUsername}
            placeholder={hideCredentials ? HIDDEN_STRING : 'ex. admin@gladly.com'}
            value={username}
          />
          <InputHeader>Password</InputHeader>
          <StyledInput onChange={this.editPassword} placeholder={HIDDEN_STRING} value={password} type="password" />
          <InputHeader>Realm</InputHeader>
          <StyledInput
            onChange={this.editRealm}
            placeholder={hideCredentials ? HIDDEN_STRING : 'realm'}
            value={realm}
          />
          <BoldHeader>HTTP headers</BoldHeader>
          <WebhookError errors={_.get(this.state, 'errors.headers')} />
          {this.renderHeaders()}
          <AddHeaderButton onClick={this.addHeaderRow} buttonType={ButtonTypes.TEXT} blurOnClick>
            + http header
          </AddHeaderButton>
          <LinkContainer>
            <InputHeader>If you have any questions, try taking a look at our guides</InputHeader>
            <WebhooksLink href="https://developer.gladly.com/rest/#tag/Summary">
              https://developer.gladly.com/rest/#tag/Summary
            </WebhooksLink>
          </LinkContainer>
          <FooterSpacer />
        </div>
        <FooterContainer>
          <FooterButtonsContainer>
            <WebhookError
              errors={_.map(
                _.filter(this.props.errors, error => !error.attr),
                'detail'
              )}
            />
            <Button onClick={this.handleClickCancel} buttonType={ButtonTypes.TEXT} blurOnClick>
              Cancel
            </Button>

            <Button onClick={this.handleClickSave} buttonType={ButtonTypes.PRIMARY} blurOnClick data-aid="webhook-save">
              Save webhook
            </Button>
          </FooterButtonsContainer>
        </FooterContainer>
      </Background>
    );
  }
}

WebhookEditor.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.instanceOf(Err)),
  isExisting: PropTypes.bool.isRequired,
  isPending: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  webhook: PropTypes.instanceOf(Webhook),
};

function mapStateToProps({ getProvider }, { webhookId }) {
  let webhookProvider = getProvider('webhooks');
  const webhook = webhookProvider.findBy({ id: webhookId });
  let errors = webhookId ? webhookProvider.getErrors(webhookId) : webhookProvider.getErrorsForNew();

  return {
    errors,
    isPending: false,
    isExisting: !!webhookId,
    webhook,
  };
}

function mapExecuteToProps(executeAction) {
  return {
    onCancel,
    onSave,
  };

  function onSave(webhook, isExisting) {
    executeAction(SaveWebhook, { webhook, isExisting });
  }

  function onCancel() {
    executeAction(OpenWebhooks);
  }
}

export default connect(mapStateToProps, mapExecuteToProps)(WebhookEditor);



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/admin/integrations/webhook_editor.jsx